import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema } from './common.js';
import { creditSchema } from './creators.js';
import { collectionItemSchema, issueSchema, seriesSchema, wishlistItemSchema } from './entities.js';
import { publicationFormatSchema, seriesStatusSchema } from './enums.js';
// ------------------------------------------------------------- list queries
export const sortDirectionSchema = z.enum(['asc', 'desc']).default('asc');
/** Treats "" and "all" as "no filter", which is what the UI sends. */
const optionalFilter = (schema) => z.preprocess((value) => (value === '' || value === 'all' ? undefined : value), schema.optional());
export const seriesListQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(200).optional(),
    publisherId: optionalFilter(idSchema),
    status: optionalFilter(seriesStatusSchema),
    format: optionalFilter(publicationFormatSchema),
    language: optionalFilter(z.string().trim().max(5)),
    /** `progress` sorts by how complete a run is, which is the interesting one. */
    sort: z.enum(['title', 'startYear', 'createdAt', 'progress']).default('title'),
    order: sortDirectionSchema,
});
export const collectionListQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(200).optional(),
    seriesId: optionalFilter(idSchema),
    publisherId: optionalFilter(idSchema),
    format: optionalFilter(publicationFormatSchema),
    language: optionalFilter(z.string().trim().max(5)),
    location: optionalFilter(z.string().trim().max(120)),
    isRead: optionalFilter(z.enum(['true', 'false'])),
    sort: z.enum(['addedAt', 'series', 'releaseDate', 'number']).default('addedAt'),
    order: sortDirectionSchema.default('desc'),
});
// -------------------------------------------------------------- list models
/** One row of the series overview, with the numbers the gap bar needs. */
export const seriesListItemSchema = seriesSchema.extend({
    publisherName: z.string().nullable(),
    issueCount: z.number().int().nonnegative(),
    ownedCount: z.number().int().nonnegative(),
    /** expectedIssueCount when set, otherwise the number of known issues. */
    plannedCount: z.number().int().nonnegative(),
    /** The series' own picture, else the cover of its first volume that has one. */
    displayCoverImageId: idSchema.nullable(),
});
/** One tile of the collection grid: the physical copy plus its context. */
export const collectionRowSchema = z.object({
    item: collectionItemSchema,
    issue: issueSchema,
    seriesId: idSchema,
    seriesTitle: z.string(),
    publisherName: z.string().nullable(),
    coverImageId: idSchema.nullable(),
});
/** An issue inside a series view, including how many copies are owned. */
export const seriesIssueSchema = issueSchema.extend({
    ownedQuantity: z.number().int().nonnegative(),
    collectionItemIds: z.array(idSchema),
    barcodeCount: z.number().int().nonnegative(),
});
export const seriesDetailSchema = z.object({
    series: seriesListItemSchema,
    issues: z.array(seriesIssueSchema),
    /** Regular numbers from 1..plannedCount that no issue covers. */
    missingNumbers: z.array(z.string()),
    locations: z.array(z.string()),
    /** Everyone credited on any volume, ordered by role. */
    creators: z.array(creditSchema),
    /** The run is being loaded from a source right now (list first, then covers). */
    completing: z.boolean(),
});
/** A wishlist entry with what the list shows: series, number, cover. */
export const wishlistRowSchema = wishlistItemSchema.extend({
    seriesTitle: z.string().nullable(),
    issueNumber: z.string().nullable(),
    /** The series the entry belongs to, for the link; set for issues too. */
    targetSeriesId: idSchema.nullable(),
    /** The issue's cover, else the series picture. */
    coverImageId: idSchema.nullable(),
});
// ------------------------------------------------------------ write models
export const quickAddRequestSchema = z.object({
    /** Free-form range, e.g. "1-25, 30, 32". */
    range: z.string().trim().min(1).max(500),
    variantName: z.string().trim().max(150).nullish(),
    /** Also create a collection item per issue, i.e. "I own these". */
    addToCollection: z.boolean().default(true),
    location: z.string().trim().max(120).nullish(),
    condition: z.string().trim().max(50).nullish(),
});
export const quickAddResponseSchema = z.object({
    created: z.array(issueSchema),
    /** Numbers that already existed and were left untouched. */
    skipped: z.array(z.string()),
    errors: z.array(z.string()),
});
export const bulkCollectionUpdateSchema = z
    .object({
    ids: z.array(idSchema).min(1).max(500),
    location: z.string().trim().max(120).nullish(),
    isRead: z.boolean().optional(),
    condition: z.string().trim().max(50).nullish(),
})
    .refine((value) => value.location !== undefined || value.isRead !== undefined || value.condition !== undefined, { message: 'Mindestens ein Feld muss geändert werden' });
export const facetsSchema = z.object({
    publishers: z.array(z.object({ id: idSchema, name: z.string(), count: z.number().int() })),
    locations: z.array(z.object({ value: z.string(), count: z.number().int() })),
    formats: z.array(z.object({ value: publicationFormatSchema, count: z.number().int() })),
    languages: z.array(z.object({ value: z.string(), count: z.number().int() })),
});
export const dashboardStatsSchema = z.object({
    issueCount: z.number().int(),
    copyCount: z.number().int(),
    seriesCount: z.number().int(),
    wishlistCount: z.number().int(),
    purchaseValue: z.number(),
    readCount: z.number().int(),
    recentlyAdded: z.array(collectionRowSchema),
    almostComplete: z.array(seriesListItemSchema),
});
// ------------------------------------------------------- import and export
export const exportDocumentSchema = z.object({
    version: z.literal(1),
    exportedAt: isoDateTimeSchema,
    publishers: z.array(z.unknown()),
    series: z.array(z.unknown()),
    issues: z.array(z.unknown()),
    collectionItems: z.array(z.unknown()),
    wishlistItems: z.array(z.unknown()),
    barcodes: z.array(z.unknown()),
});
export const importResultSchema = z.object({
    publishers: z.number().int(),
    series: z.number().int(),
    issues: z.number().int(),
    collectionItems: z.number().int(),
    wishlistItems: z.number().int(),
    barcodes: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.string()),
});
/** Shape of one CSV row, also used as the documented column order. */
export const csvRowSchema = z.object({
    series: z.string(),
    volume: z.string(),
    publisher: z.string(),
    format: z.string(),
    language: z.string(),
    number: z.string(),
    variant: z.string(),
    title: z.string(),
    releaseDate: z.union([isoDateSchema, z.literal('')]),
    price: z.string(),
    quantity: z.string(),
    condition: z.string(),
    purchasePrice: z.string(),
    purchaseDate: z.union([isoDateSchema, z.literal('')]),
    location: z.string(),
    isRead: z.string(),
    notes: z.string(),
    barcode: z.string(),
});
export const CSV_COLUMNS = Object.keys(csvRowSchema.shape);
//# sourceMappingURL=views.js.map